import { policyFor, repairLine, worded } from "./rule-catalog";
import {
  exposureZoneAt,
  getVisibilityRuleLeaves,
  isValidExposureBoundary,
  resolveExposureMarkIndexes,
} from './index';
import type {
  QuestionnaireExposureMarkIndexes,
  QuestionnaireExposureZone,
  QuestionnaireValidationIssue,
} from './index';
import { questionAssetSurfaces } from './question-asset-surfaces';
import { studyBindingFor } from './study-binding';
import { studySpecOf } from './study-spec';
import { opensLaneSlot } from '../study-design';

type RecordLike = Record<string, any>;
const record = (value: unknown): RecordLike =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const list = (value: unknown): Array<RecordLike> =>
  Array.isArray(value) ? value.filter(v => v && typeof v === 'object') : [];

/**
 * The tested assets a study shows twins: the catalog entries its StudySpec
 * resolves as `stimulus` materials. A catalog such as a list of pains is
 * never shown, so it is never exposure, and a draft without a spec has no
 * stimulus. Every exposure check reads this one set.
 */
export function stimulusAssetsOf(draft: unknown): { assets: Array<RecordLike> } {
  const source = record(draft);
  const stimulus = new Set(
    (studySpecOf(source)?.resolved.materials ?? [])
      .filter(material => material.role === 'stimulus')
      .map(material => material.assetId),
  );
  return { assets: list(record(source.testedAssets).assets).filter(a => stimulus.has(a.id)) };
}

/**
 * A question surface that directly renders one of `testedAssets` before the
 * runner can apply the declared reveal: a binding, or rows that are the tested
 * assets. Asset refs identify answer/report subjects and render nothing, so a
 * typed asset-choice roster (`assetBinding.mode="none"` with option refs) is
 * not a surface here; listed before the reveal it is refused on its own
 * account (`pre_exposure_asset_listed`, decision 24), since it would list what
 * is tested before anyone sees it.
 */
export function preRevealStimulusSurface(
  question: unknown,
  testedAssets: unknown,
): 'assetBinding' | 'response.rows' | null {
  const surfaces = questionAssetSurfaces(question, testedAssets);
  if (surfaces.some(surface => surface.kind === 'binding' && surface.assetIds.length > 0)) {
    return 'assetBinding';
  }
  return surfaces.some(surface => surface.kind === 'tested_rows' && surface.assetIds.length > 0)
    ? 'response.rows'
    : null;
}

/** A question-level ref with one of these roles names versions shown together. */
const JOINT_REF_ROLES = new Set<string | null>(['matrix_row', 'choice', 'comparison']);

/**
 * Exact asset identities a question carries, read from `questionAssetSurfaces`:
 * every asset it references (`referenced`); those it shows together as a list
 * (`joint`: answers, categories, rows, measures, tested rows, and question refs
 * in a joint role); and those its binding shows (`bound`).
 */
export function questionExposureAssetIds(question: unknown, testedAssets: unknown): {
  referenced: Array<string>; joint: Array<string>; bound: Array<string>;
} {
  const referenced = new Set<string>();
  const joint = new Set<string>();
  const bound: Array<string> = [];
  questionAssetSurfaces(question, testedAssets).forEach(surface => {
    if (surface.kind === 'binding') {
      bound.push(...surface.assetIds);
      return;
    }
    const ids = surface.kind === 'tested_rows' ? surface.assetIds : [surface.assetId];
    ids.forEach(id => {
      referenced.add(id);
      if (surface.kind !== 'ref' || JOINT_REF_ROLES.has(surface.role)) joint.add(id);
    });
  });
  return { referenced: [...referenced], joint: [...joint], bound };
}

/**
 * Where a question lists a stimulus asset as one of its answers, categories,
 * rows or joint-role refs (`questionExposureAssetIds(...).joint`): the first
 * such surface's path, or null.
 */
function jointListPath(question: unknown, stimulus: unknown): string | null {
  const surface = questionAssetSurfaces(question, stimulus).find(
    candidate =>
      candidate.kind === 'item' ||
      (candidate.kind === 'tested_rows' && candidate.assetIds.length > 0) ||
      (candidate.kind === 'ref' && JOINT_REF_ROLES.has(candidate.role)),
  );
  return surface ? surface.path : null;
}

/**
 * The tested versions a question shows at once. An isolated design (or the
 * question's own per-asset binding) narrows catalog-wide candidates to one
 * version per lane, so only its joint surfaces (rows, choices, comparison
 * refs) count; otherwise its bound and referenced assets are shown together
 * too. The lane rule below and the derived shared-view mark
 * (`exposureCeilingIndex`, Nest) read this one fact.
 */
export function simultaneousExposureAssetIds(
  question: unknown,
  testedAssets: unknown,
  monadic: boolean,
): Array<string> {
  const scope = questionExposureAssetIds(question, testedAssets);
  const isolated = monadic || record(record(question).assetBinding).mode === 'per_asset';
  return [...new Set(isolated ? scope.joint : [...scope.joint, ...scope.referenced, ...scope.bound])];
}

/**
 * Whether the lane phase repeats per asset: some lane-zone question opens a
 * slot (`opensLaneSlot`) under the design. Without one the lane runs once as
 * shared context, so a question fixed on one asset is asked once about it.
 * The lane rule below, the compiler's exposure scopes and the builder's
 * exposure chips read this one rule.
 */
export function lanesRepeat(
  questions: ReadonlyArray<{ assetBinding?: unknown }>,
  indexes: QuestionnaireExposureMarkIndexes,
  monadic: boolean,
): boolean {
  return questions.some((q, index) =>
    exposureZoneAt(index, indexes) === 'lane' && opensLaneSlot(q.assetBinding, monadic));
}

/**
 * The zone each question is asked in when the survey runs its zones as
 * separate passes: the lane repeats once for each version, so the part
 * before exposure, each version and the shared view are each their own pass.
 * Null when the survey is one pass: an undeclared/invalid boundary, a draft
 * without stimulus, no lane, or a lane that runs once. A condition or a skip
 * may then cross zones. The dependency rule below, and what skip-logic
 * detection tells its model, read this one fact.
 */
export function questionPassZones(
  draft: unknown,
  design?: string,
): ReadonlyMap<string, QuestionnaireExposureZone> | null {
  const source = record(draft);
  if (!isValidExposureBoundary(source.exposureBoundary)) return null;
  if (!stimulusAssetsOf(source).assets.length) return null;
  const questions = list(source.researchQuestions);
  const indexes = resolveExposureMarkIndexes(questions, source.exposureBoundary);
  if (indexes.revealIndex >= indexes.sharedViewIndex) return null;
  const monadic = (design ?? studySpecOf(source)?.resolved.design) === 'twin_monadic';
  if (!lanesRepeat(questions, indexes, monadic)) return null;
  return new Map(
    questions.map((question, index) => [String(question.stableKey), exposureZoneAt(index, indexes)]),
  );
}

/**
 * Whether one question's answer is available where another question runs.
 *
 * The repeated lane is executed once for each version.  Its answers therefore
 * have no single value in the subsequent shared pass.  Keep that one
 * execution fact here so authoring affordances and the write validator make
 * the same decision.  An undeclared/invalid boundary, or a draft without
 * stimulus, has no declared exposure scope and leaves ordinary dependencies
 * available for repair.
 */
export function isQuestionDependencyAvailableAtExposure(
  draft: unknown,
  targetStableKey: string,
  sourceStableKey: string,
  design?: string,
): boolean {
  const zones = questionPassZones(draft, design);
  return !(
    zones?.get(targetStableKey) === 'shared_view' &&
    zones?.get(sourceStableKey) === 'lane'
  );
}

/**
 * Validate declared time against explicit scope BEFORE a stamp can erase a
 * contradiction. Missing boundaries are legacy/undeclared input, not invented
 * marks. All new compiled instruments carry the existing versioned boundary.
 *
 * Scope is read over the study's stimulus (`stimulusAssetsOf`). A study that
 * shows twins nothing has no exposure to order: its stored marks are neither
 * checked nor shown, so they never refuse an edit.
 *
 * `design` is the compile's; otherwise the draft's StudySpec design (plan
 * Stage 3, S3.4 row 22). A draft without a spec reads as a study with no
 * recorded choice, comparative, as the compile does.
 */
export function exposureCoherenceIssues(draft: unknown, design?: string): Array<QuestionnaireValidationIssue> {
  const source = record(draft);
  const boundary = source.exposureBoundary;
  if (boundary === undefined || boundary === null) return [];
  const issues: Array<QuestionnaireValidationIssue> = [];
  const fail = (
    path: string,
    message: string,
    code:
      | 'exposure_boundary_invalid'
      | 'exposure_mark_question_missing'
      | 'exposure_marks_cross'
      | 'pre_exposure_question_binds_stimulus'
      | 'pre_exposure_asset_listed'
      | 'lane_question_shows_several_versions'
      | 'shared_view_question_per_version'
      | 'lane_question_fixed_asset_subset'
      | 'shared_view_depends_on_lane_answer'
      | 'route_target_other_pass',
  ) => issues.push({ path, code, severity: 'error', message });
  if (!isValidExposureBoundary(boundary)) {
    fail('exposureBoundary', repairLine('exposure_boundary_invalid'), 'exposure_boundary_invalid');
    return issues;
  }
  const stimulus = stimulusAssetsOf(source);
  const catalog = stimulus.assets;
  if (!catalog.length) return issues;
  const questions = list(source.researchQuestions);
  for (const mark of ['reveal', 'sharedView'] as const) {
    const key = boundary[mark]?.stableKey;
    if (key && !questions.some(q => q.stableKey === key)) {
      fail(`exposureBoundary.${mark}`, repairLine('exposure_mark_question_missing', { mark: String(mark), key: String(key) }), 'exposure_mark_question_missing');
    }
  }
  const indexes = resolveExposureMarkIndexes(questions, boundary);
  if (indexes.revealIndex >= indexes.sharedViewIndex) {
    fail('exposureBoundary', worded('exposure_marks_cross', 'Shared view cannot start before the reveal. No question is both pre-exposure and shared view.'), 'exposure_marks_cross');
  }
  if (issues.length) return issues;
  const monadic = (design ?? studySpecOf(source)?.resolved.design) === 'twin_monadic';
  const passes = lanesRepeat(questions, indexes, monadic);
  // A monadic design shows each version on its own only in lanes that
  // repeat. With no lane question that opens one, the engine runs a single
  // pass with every version shown together: a comparison under the monadic
  // design. A lane question the author left unbound runs with the binding
  // normalize stamps on it (`studyBindingFor`), so the rule reads the same
  // before and after the stamp. Raised at the user's severity, the advice
  // the builder shows; a model's batch is refused (`blocksWrite`).
  const spec = studySpecOf(source);
  const runsEachVersion = lanesRepeat(
    questions.map((q, index) => ({
      assetBinding: q.assetBinding ?? studyBindingFor(q, exposureZoneAt(index, indexes), spec),
    })),
    indexes,
    monadic,
  );
  if (monadic && !runsEachVersion) {
    issues.push({
      path: 'exposureBoundary',
      code: 'monadic_design_without_lane',
      severity: policyFor('monadic_design_without_lane', 'user') === 'warn' ? 'warning' : 'error',
      message: repairLine('monadic_design_without_lane'),
    });
  }
  // When the lanes repeat, the engine asks the shared view in its own pass,
  // seeded with the answers given before the reveal and never a lane's
  // (`monadic.build_sub_survey`, `prior_answers`): each version has its own
  // answer, so none is the answer there. A condition, route or piped list on
  // a lane question never holds, and the question would never be asked.
  questions.forEach((q, index) => {
    const zone = exposureZoneAt(index, indexes);
    const scope = questionExposureAssetIds(q, stimulus);
    const path = `researchQuestions[${index}]`;
    // Constraint 2: a direct question-level binding or tested-asset grid
    // cannot run before the reveal. Otherwise, a question whose answers,
    // categories, rows or joint-role refs are stimulus assets cannot either
    // (decision 24): it would list what is tested before anyone sees it, and
    // its answers read as the assets do (a message's copy, a product's
    // price). A brief that lists them before the reveal uses plain named
    // options.
    if (zone === 'pre') {
      const preRevealSurface = preRevealStimulusSurface(q, stimulus);
      const listed = preRevealSurface === null ? jointListPath(q, stimulus) : null;
      if (preRevealSurface !== null) {
        fail(`${path}.${preRevealSurface}`, repairLine('pre_exposure_question_binds_stimulus'), 'pre_exposure_question_binds_stimulus');
      } else if (listed !== null) {
        fail(`${path}.${listed}`, repairLine('pre_exposure_asset_listed'), 'pre_exposure_asset_listed');
      }
    }
    // Its own code, so the refusal says why (QA3 F11 review P5): a question
    // that shows each twin its own version cannot run once every version is
    // on screen. Its binding is never released into the shared view: which
    // version it asks about would be ambiguous there.
    if (zone === 'shared_view' && q.assetBinding?.mode === 'per_asset' && scope.bound.length > 1) {
      fail(path, repairLine('shared_view_question_per_version'), 'shared_view_question_per_version');
    }
    const dependsOn = [
      ...getVisibilityRuleLeaves(q.visibilityRule),
      ...getVisibilityRuleLeaves(record(q.routingRule).when),
    ].map(leaf => record(leaf).dependsOnStableKey)
      .concat(record(record(q.response).choicesFrom).sourceStableKey);
    if (dependsOn.some(key =>
      typeof key === 'string' &&
      !isQuestionDependencyAvailableAtExposure(source, q.stableKey, key, design),
    )) {
      fail(path, repairLine('shared_view_depends_on_lane_answer'), 'shared_view_depends_on_lane_answer');
    }
    // The same passes run each zone on its own (before the reveal, once per
    // version, the shared view), so a skip lands only inside its own zone.
    const route = record(q.routingRule);
    if (passes && route.action === 'skip_to') {
      const targetIndex = questions.findIndex(t => t.stableKey === route.targetStableKey);
      if (targetIndex >= 0 && exposureZoneAt(targetIndex, indexes) !== zone) {
        fail(path, repairLine('route_target_other_pass'), 'route_target_other_pass');
      }
    }
    if (zone !== 'lane') return;
    // The engine's rule (`monadic._declared_exposure_phases`), which admits
    // the run: an unbound question fixed on some of the versions has no place
    // in the lane phase, whether the lanes repeat (it would be asked in every
    // lane) or run once. Its own code, so the refusal says why, and so the
    // write gate can let the write that adds an asset commit before the
    // questions take it in, when every version the question leaves out is
    // one that write adds (the backend's `causedByAddedAssets`;
    // `tests/fixtures/study-spec/twin-exposure-cases.json` holds both
    // languages to the rule).
    if (q.assetBinding?.mode === 'none' && scope.referenced.length > 0 && scope.referenced.length < catalog.length) {
      fail(path, repairLine('lane_question_fixed_asset_subset'), 'lane_question_fixed_asset_subset');
    }
    // Catalog-wide stimulus refs are candidates narrowed by an isolated lane;
    // actual asset rows/choices/comparison refs are visible together and are not.
    if (simultaneousExposureAssetIds(q, stimulus, monadic).length > 1) {
      fail(path, repairLine('lane_question_shows_several_versions'), 'lane_question_shows_several_versions');
    }
  });
  return issues;
}
